import { emitFlagMessage } from '@cae/cobalt-react/components/flag-message';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { useNavigationBlock } from '@shared/hooks/use-navigation-block/useNavigationBlock';
import * as React from 'react';

import type { EditIdentityFormData } from '../../schemas/editIdentityPanel.schema';
import type {
  IdentityFormErrors,
  IdentityFormState,
  UseEditIdentityPanelOptions,
  UseEditIdentityPanelReturn,
} from './useEditIdentityPanel.types';

import {
  BIRTHDATE_FORMAT_ERROR,
  editIdentitySchema,
} from '../../schemas/editIdentityPanel.schema';
import { useBirthdateField } from '../use-birthdate-field/useBirthdateField';
import { useProfile } from '../use-profile/useProfile';
import { useUpdateProfileIdentity } from '../use-update-profile-identity/useUpdateProfileIdentity';
import {
  buildInitialFormState,
  parseBirthdateInput,
  toIsoDateOnly,
} from './useEditIdentityPanel.utils';

export function useEditIdentityPanel({
  onClose,
}: UseEditIdentityPanelOptions): UseEditIdentityPanelReturn {
  const { profile, isProfileLoading } = useProfile();
  const { mutate, isPending, isError, isSuccess } = useUpdateProfileIdentity();
  const isMobile = useMediaQuery(MediaQuery.Mobile);

  const [isConfirmOpen, setIsConfirmOpen] = React.useState<boolean>(false);
  const [isExitModalOpen, setIsExitModalOpen] = React.useState<boolean>(false);

  const [formState, setFormState] = React.useState<IdentityFormState>(() =>
    buildInitialFormState(profile)
  );

  const [errors, setErrors] = React.useState<IdentityFormErrors>({});

  const {
    birthdateRawInputRef,
    birthdateInputDirty,
    handleDateInputChange,
    handleDateSelect,
  } = useBirthdateField(setFormState, setErrors);

  const initialized = React.useRef(false);
  const initialFormStateRef = React.useRef<IdentityFormState>(formState);
  const sanitizedStateRef = React.useRef<EditIdentityFormData | null>(null);
  const isPageRefreshingRef = React.useRef(false);
  const isDiscardingRef = React.useRef(false);

  React.useEffect(() => {
    const handleBeforeUnload = (): void => {
      isPageRefreshingRef.current = true;
    };
    globalThis.addEventListener('beforeunload', handleBeforeUnload);
    return (): void => {
      globalThis.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  React.useEffect(() => {
    if (isSuccess) {
      emitFlagMessage('success', 'Changes saved successfully!', 5000);
      onClose();
    }
  }, [isSuccess, onClose]);

  React.useEffect(() => {
    if (profile && !initialized.current) {
      initialized.current = true;
      const initial = buildInitialFormState(profile);
      initialFormStateRef.current = initial;
      setFormState(initial);
    }
  }, [profile]);

  const handleFieldChange = React.useCallback(
    (
      field: keyof Omit<IdentityFormState, 'birthdate'>,
      value: string
    ): void => {
      setFormState(prev => ({ ...prev, [field]: value }) as IdentityFormState);
      setErrors(
        prev => ({ ...prev, [field]: undefined }) as IdentityFormErrors
      );
    },
    []
  );

  const validate = React.useCallback((): boolean => {
    const raw = birthdateRawInputRef.current;
    const nextErrors: IdentityFormErrors = {};
    let birthdateForValidation: Date | null = formState.birthdate;

    if (raw !== '') {
      const parsed = parseBirthdateInput(raw);
      if (parsed !== null) {
        birthdateForValidation = parsed;
      } else {
        nextErrors.birthdate = BIRTHDATE_FORMAT_ERROR;
      }
    }

    const result = editIdentitySchema.safeParse({
      ...formState,
      birthdate: birthdateForValidation,
    });

    if (!result.success) {
      for (const issue of result.error.issues) {
        const field = issue.path[0] as keyof IdentityFormErrors;
        if (!nextErrors[field]) {
          nextErrors[field] = issue.message;
        }
      }
    }

    if (Object.keys(nextErrors).length > 0 || !result.success) {
      setErrors(nextErrors);
      return false;
    }

    sanitizedStateRef.current = result.data;
    setErrors({});
    return true;
  }, [formState, birthdateRawInputRef]);

  const isDirty =
    !isSuccess &&
    (formState.salutation !== initialFormStateRef.current.salutation ||
      formState.givenName !== initialFormStateRef.current.givenName ||
      formState.middleName !== initialFormStateRef.current.middleName ||
      formState.lastName !== initialFormStateRef.current.lastName ||
      formState.birthdate?.getTime() !==
        initialFormStateRef.current.birthdate?.getTime() ||
      birthdateInputDirty);

  const isDirtyRef = React.useRef(isDirty);
  isDirtyRef.current = isDirty;

  useNavigationBlock(
    React.useCallback(
      (): boolean => isDirtyRef.current && !isDiscardingRef.current,
      []
    )
  );

  const handleSubmit = React.useCallback((): void => {
    if (!isDirtyRef.current || !validate()) return;
    setIsConfirmOpen(true);
  }, [validate]);

  const handleConfirm = React.useCallback((): void => {
    if (!sanitizedStateRef.current) return;
    setIsConfirmOpen(false);
    const sanitized = sanitizedStateRef.current;
    mutate({
      salutation: sanitized.salutation || null,
      givenName: sanitized.givenName,
      middleName: sanitized.middleName || null,
      lastName: sanitized.lastName,
      birthdate: sanitized.birthdate
        ? toIsoDateOnly(sanitized.birthdate)
        : null,
    });
  }, [mutate]);

  const handleCancelConfirm = React.useCallback((): void => {
    setIsConfirmOpen(false);
  }, []);

  const handleCloseAttempt = React.useCallback((): void => {
    if (isPageRefreshingRef.current) return;
    if (isPending) return;
    if (isDirtyRef.current) {
      setIsExitModalOpen(true);
    } else {
      onClose();
    }
  }, [isPending, onClose]);

  const handleExit = React.useCallback((): void => {
    isDiscardingRef.current = true;
    setIsExitModalOpen(false);
    onClose();
  }, [onClose]);

  const handleKeepEditing = React.useCallback((): void => {
    setIsExitModalOpen(false);
  }, []);

  const isProfileReady = !!profile;

  return {
    formState,
    errors,
    isMobile,
    isConfirmOpen,
    isExitModalOpen,
    isDirty,
    isPending,
    isError,
    isProfileLoading,
    isProfileReady,
    handleFieldChange,
    handleDateSelect,
    handleDateInputChange,
    handleSubmit,
    handleConfirm,
    handleCancelConfirm,
    handleCloseAttempt,
    handleExit,
    handleKeepEditing,
  };
}
